<template>
  <div class="promotion-wrapper">
    <div class="promotion-container">
      <!-- 加载中 -->
      <div v-if="pageLoading" class="loading-tip">
        <el-card class="tip-card">
          <div class="tip-content">
            <i class="el-icon-loading"></i>
            <p>信息获取中...</p>
          </div>
        </el-card>
      </div>

      <!-- 非加载状态 -->
      <template v-else>
        <!-- VIP 查询区域（始终显示，确保主体切换可用） -->
        <div class="vip-query-section">
          <div class="section-header">
            <h3>VIP 查询</h3>
          </div>
          <el-card class="query-card">
            <!-- 主体切换 -->
            <div class="form-item">
              <el-radio-group v-model="dataSource" size="small" @change="handleDataSourceChange">
                <el-radio-button label="wanxin">玩心</el-radio-button>
                <el-radio-button label="yuewan">悦玩</el-radio-button>
              </el-radio-group>
            </div>

            <!-- 游戏信息未绑定提示 -->
            <div v-if="!isGameBound" class="not-bound-inline">
              <i class="el-icon-warning-outline"></i>
              <p>当前主体暂无绑定的游戏信息</p>
            </div>

            <!-- 游戏信息已绑定 -->
            <template v-else>
              <!-- 选择游戏 -->
              <div class="form-item">
                <el-select
                  v-model="selectedGame"
                  placeholder="请选择游戏"
                  filterable
                  style="width: 100%"
                  @change="handleGameChange"
                >
                  <el-option
                    v-for="game in gameOptions"
                    :key="game.major_id"
                    :label="game.major_name"
                    :value="game.major_id"
                  ></el-option>
                </el-select>
              </div>

              <!-- 选择角色 -->
              <div class="form-item">
                <el-select
                  v-model="selectedRole"
                  placeholder="选择角色（当选择游戏后有值）"
                  filterable
                  style="width: 100%"
                  :disabled="!selectedGame"
                >
                  <el-option
                    v-for="role in roleOptions"
                    :key="role.role_id"
                    :label="`${role.role_name}（${role.role_id}）`"
                    :value="role.role_id"
                  ></el-option>
                </el-select>
              </div>

              <!-- 确定按钮 -->
              <el-button
                type="primary"
                style="width: 100%"
                :loading="queryLoading"
                @click="handleConfirmQuery"
              >
                确定
              </el-button>
            </template>
          </el-card>
        </div>

          <!-- 提交促单及结算区域 -->
          <div v-if="showPromotionForm" class="promotion-form-section">
            <div class="section-header">
              <h3>提交促单及结算</h3>
            </div>
            <el-card class="promotion-card">
              <!-- 促单发起/结算时间 -->
              <div class="form-item">
                <label class="form-label">开始时间</label>
                <div class="datetime-row">
                  <el-date-picker
                    v-model="startDate"
                    type="date"
                    placeholder="选择日期"
                    value-format="yyyy-MM-dd"
                    class="datetime-date"
                    @change="handleStartDateChange"
                  ></el-date-picker>
                  <el-time-picker
                    v-model="startTimeVal"
                    placeholder="时间"
                    value-format="HH:mm:ss"
                    class="datetime-time"
                    @change="handleTimeChange"
                  ></el-time-picker>
                </div>
              </div>
              <div class="form-item">
                <label class="form-label">结束时间</label>
                <div class="datetime-row">
                  <el-date-picker
                    v-model="endDate"
                    type="date"
                    placeholder="选择日期"
                    value-format="yyyy-MM-dd"
                    class="datetime-date"
                    :picker-options="endDatePickerOptions"
                    @change="handleTimeChange"
                  ></el-date-picker>
                  <el-time-picker
                    v-model="endTimeVal"
                    placeholder="时间"
                    value-format="HH:mm:ss"
                    class="datetime-time"
                    @change="handleTimeChange"
                  ></el-time-picker>
                </div>
              </div>

              <!-- 选择订单区域 -->
              <div v-if="orderData.length > 0" class="order-section">
                <div class="order-header">
                  <span class="order-title">选择订单</span>
                  <el-checkbox v-model="selectAll" @change="handleSelectAll" class="select-all-checkbox">全部</el-checkbox>
                </div>

                <!-- 日期 Tab -->
                <div class="date-tabs">
                  <el-radio-group v-model="activeDateTab" size="small">
                    <el-radio-button
                      v-for="group in orderData"
                      :key="group.date"
                      :label="group.date"
                    >
                      {{ formatDateTab(group.date) }}<el-badge v-if="getDateOrderCount(group.date)" :value="getDateOrderCount(group.date)" class="tab-count-badge" />
                    </el-radio-button>
                  </el-radio-group>
                </div>

                <!-- 订单列表 -->
                <div class="order-list" v-loading="orderLoading">
                  <!-- 当日全选行 -->
                  <div class="date-select-all">
                    <el-checkbox
                      :value="isCurrentDateAllSelected"
                      @change="handleDateSelectAll"
                    >
                      当日全选
                    </el-checkbox>
                    <span class="order-count">{{ currentDateOrders.length }} 笔订单</span>
                  </div>
                  <!-- 订单卡片 -->
                  <div
                    v-for="order in currentDateOrders"
                    :key="order.order_sn"
                    class="order-card"
                    :class="{ selected: isOrderSelected(order.order_sn) }"
                    @click="handleOrderSelect(order)"
                  >
                    <div class="order-card-check">
                      <el-checkbox
                        :value="isOrderSelected(order.order_sn)"
                        @click.native.stop
                        @change="handleOrderSelect(order)"
                      ></el-checkbox>
                    </div>
                    <div class="order-card-body">
                      <div class="order-card-main">
                        <span class="order-money">{{ order.money }}</span>
                        <span class="order-money-unit">元</span>
                      </div>
                      <div class="order-card-sub" v-if="order.goods_name">{{ order.goods_name }}</div>
                    </div>
                    <div class="order-card-time">{{ formatOrderTime(order.update_time) }}</div>
                  </div>
                  <div v-if="currentDateOrders.length === 0" class="table-empty">
                    <i class="el-icon-document"></i>
                    <p>暂无订单</p>
                  </div>
                </div>
              </div>

              <!-- 订单加载中 -->
              <div v-if="orderLoading && orderData.length === 0" class="order-loading">
                <i class="el-icon-loading"></i> 加载订单中...
              </div>

              <!-- 促单金额 -->
              <div class="promotion-amount">
                <div class="amount-left">
                  <span class="amount-label">促单金额</span>
                  <span class="amount-count">已选 {{ selectedOrders.length }} 笔</span>
                </div>
                <div class="amount-right">
                  <span class="amount-symbol">&#xFFE5;</span>
                  <span class="amount-value">{{ promotionAmount }}</span>
                </div>
              </div>

              <!-- 促单发起人 -->
              <div class="form-item">
                <el-select
                  v-model="promoterId"
                  placeholder="请选择促单发起人"
                  filterable
                  style="width: 100%"
                >
                  <el-option
                    v-for="item in promoterList"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </div>

              <!-- 促单类型 -->
              <div class="form-item">
                <el-select
                  v-model="promotionType"
                  placeholder="请选择促单类型"
                  filterable
                  style="width: 100%"
                >
                  <el-option
                    v-for="item in promotionTypeList"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </div>

              <!-- 促单方案 -->
              <div class="form-item">
                <el-input
                  v-model="promotionPlan"
                  type="textarea"
                  placeholder="请输入促单方案"
                  :maxlength="100"
                  show-word-limit
                  :rows="3"
                ></el-input>
              </div>

              <!-- 增加一个拉取会话时间 的选择框 -->
              <div class="form-item">
                <label class="form-label">拉取会话开始时间</label>
                <div class="datetime-row">
                  <el-date-picker
                    v-model="chatLogStartDate"
                    type="date"
                    placeholder="选择日期"
                    value-format="yyyy-MM-dd"
                    class="datetime-date"
                    @change="handleChatLogStartDateChange"
                  ></el-date-picker>
                  <el-time-picker
                    v-model="chatLogStartTimeVal"
                    placeholder="时间"
                    value-format="HH:mm:ss"
                    class="datetime-time"
                    @change="handleChatLogTimeChange"
                  ></el-time-picker>
                </div>
              </div>
              <div class="form-item">
                <label class="form-label">拉取会话结束时间</label>
                <div class="datetime-row">
                  <el-date-picker
                    v-model="chatLogEndDate"
                    type="date"
                    placeholder="选择日期"
                    value-format="yyyy-MM-dd"
                    class="datetime-date"
                    :picker-options="chatLogEndDatePickerOptions"
                    @change="handleChatLogTimeChange"
                  ></el-date-picker>
                  <el-time-picker
                    v-model="chatLogEndTimeVal"
                    placeholder="时间"
                    value-format="HH:mm:ss"
                    class="datetime-time"
                    @change="handleChatLogTimeChange"
                  ></el-time-picker>
                </div>
              </div>

              <!-- 操作按钮 -->
              <div class="form-actions">
                <el-button @click="handleClear" style="flex: 1">清空</el-button>
                <el-button
                  type="primary"
                  :loading="submitLoading"
                  @click="handleSubmit"
                  style="flex: 1"
                >
                  提交促单
                </el-button>
              </div>
            </el-card>
          </div>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {
  oauthLoginApi,
  getUserInfoApi,
  getQwSignatureApi,
  getVipInfoApi,
  getOrderListApi,
  getFacilitatorTypeOptionsApi,
  submitPromotionApi,
} from '@/api/promotionSubmit';
import {
  getContactUserId,
  setContactUserId,
} from '@/utils/localStorage';
// import vConsole from 'vconsole';
// new vConsole(); 
export default {
  name: 'PromotionSubmit',
  data() {
    return {
      // 企微相关
      userId: '',
      contactUserId: '',
      corpid: 'ww13459a28a657c19a',
      agentid: 1000002,

      // 页面状态
      pageLoading: true,
      isGameBound: false,
      showPromotionForm: false,

      // VIP 查询
      dataSource: 'wanxin',
      userDataList: [],
      yuewanUserDataList: [],
      vipRoleList: [],
      selectedGame: '',
      selectedRole: '',
      vipInfo: {},
      queryLoading: false,

      // 促单表单
      startDate: '',
      startTimeVal: '00:00:00',
      endDate: '',
      endTimeVal: '23:59:59',
      chatLogStartDate: '',
      chatLogStartTimeVal: '00:00:00',
      chatLogEndDate: '',
      chatLogEndTimeVal: '23:59:59',
      orderData: [],
      activeDateTab: '',
      selectedOrders: [],
      selectAll: false,
      promoterId: '',
      promotionType: '',
      promotionPlan: '',

      // 下拉选项
      promoterList: [],
      promotionTypeList: [],

      // 加载状态
      orderLoading: false,
      submitLoading: false,
    };
  },
  computed: {
    // 拼接完整的开始/结束时间字符串
    fullStartTime() {
      if (!this.startDate) return '';
      return `${this.startDate} ${this.startTimeVal || '00:00:00'}`;
    },
    fullEndTime() {
      if (!this.endDate) return '';
      return `${this.endDate} ${this.endTimeVal || '23:59:59'}`;
    },
    fullChatLogStartTime() {
      if (!this.chatLogStartDate) return '';
      return `${this.chatLogStartDate} ${this.chatLogStartTimeVal || '00:00:00'}`;
    },
    fullChatLogEndTime() {
      if (!this.chatLogEndDate) return '';
      return `${this.chatLogEndDate} ${this.chatLogEndTimeVal || '23:59:59'}`;
    },
    // 结束日期限制：开始日期后 7 天内
    endDatePickerOptions() {
      if (!this.startDate) return {};
      const start = moment(this.startDate);
      return {
        disabledDate: (date) => {
          const d = moment(date);
          return d.isBefore(start, 'day') || d.isAfter(start.clone().add(6, 'days'), 'day');
        },
      };
    },
    // 拉取会话结束日期限制：开始日期后 7 天内
    chatLogEndDatePickerOptions() {
      if (!this.chatLogStartDate) return {};
      const start = moment(this.chatLogStartDate);
      return {
        disabledDate: (date) => {
          const d = moment(date);
          return d.isBefore(start, 'day') || d.isAfter(start.clone().add(6, 'days'), 'day');
        },
      };
    },
    // 游戏选项（从 vipRoleList 提取去重）
    gameOptions() {
      if (!this.vipRoleList || this.vipRoleList.length === 0) return [];
      const gameMap = new Map();
      this.vipRoleList.forEach((role) => {
        if (!gameMap.has(role.major_id)) {
          gameMap.set(role.major_id, {
            major_id: role.major_id,
            major_name: role.major_name,
          });
        }
      });
      return Array.from(gameMap.values());
    },
    // 角色选项（根据选中游戏筛选）
    roleOptions() {
      if (!this.selectedGame || !this.vipRoleList) return [];
      return this.vipRoleList
        .filter((role) => role.major_id === this.selectedGame)
        .sort((a, b) => new Date(b.role_create_time) - new Date(a.role_create_time));
    },
    // 当前日期 tab 对应的订单
    currentDateOrders() {
      if (!this.activeDateTab || this.orderData.length === 0) return [];
      const group = this.orderData.find((g) => g.date === this.activeDateTab);
      return group ? group.order : [];
    },
    // 当前日期是否全选
    isCurrentDateAllSelected() {
      const orders = this.currentDateOrders;
      if (orders.length === 0) return false;
      return orders.every((o) => this.isOrderSelected(o.order_sn));
    },
    // 促单金额（选中订单的金额总和）
    promotionAmount() {
      if (this.selectedOrders.length === 0) return 0;
      let total = 0;
      this.selectedOrders.forEach((order) => {
        total += Number(order.money) || 0;
      });
      return total;
    },
  },
  watch: {
    // 监听选中订单变化，同步全选状态
    selectedOrders: {
      handler(val) {
        if (this.orderData.length === 0) {
          this.selectAll = false;
          return;
        }
        const allOrderSns = [];
        this.orderData.forEach((g) => g.order.forEach((o) => allOrderSns.push(o.order_sn)));
        this.selectAll = allOrderSns.length > 0 && allOrderSns.every((sn) => val.some((s) => s.order_sn === sn));
      },
      deep: true,
    },
  },
  mounted() {
    const { corpid, agentid } = this.$route.query;
    if (corpid) this.corpid = corpid;
    if (agentid) this.agentid = agentid;
    this.initContactAuth();
  },
  methods: {
    // ========== 企微客服授权（同 vipGiftRecommend） ==========
    async initContactAuth() {
      const cachedUserId = getContactUserId();
      console.log(434, cachedUserId);
      if (cachedUserId) {
        this.contactUserId = cachedUserId;
        this.loadScripts();
        return;
      }

      const { code } = this.$route.query;
      if (code) {
        console.log(443, code);
        try {
          const res = await oauthLoginApi({ code, corpid: this.corpid, agentid: this.agentid });
          if (res && res.content && res.content.contact_userid) {
            this.contactUserId = res.content.contact_userid;
            setContactUserId(res.content.contact_userid);
          }
          const query = { ...this.$route.query };
          delete query.code;
          delete query.state;
          this.$router.replace({ query });
        } catch (error) {
          console.error('[企微授权] 获取客服 userId 失败:', error);
        }
        this.loadScripts();
        return;
      }
      console.log(460, 'redirectToQwAuth');
      this.redirectToQwAuth();
    },
    redirectToQwAuth() {
      const currentUrl = window.location.href.split('?')[0];
      const params = new URLSearchParams();
      params.set('corpid', this.corpid);
      params.set('agentid', this.agentid);
      const redirectUri = encodeURIComponent(`${currentUrl}?${params.toString()}`);
      const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${this.corpid}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&agentid=${this.agentid}&state=promotionSubmit#wechat_redirect`;
      window.location.href = authUrl;
    },

    // ========== 企微 SDK ==========
    loadScripts() {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = '//wwcdn.weixin.qq.com/node/open/js/wecom-jssdk-2.0.2.js';
      script.onload = () => {
        this.wxRegister();
      };
      document.head.appendChild(script);
    },
    wxRegister() {
      console.log(this.corpid, this.agentid);
      ww.register({
        corpId: this.corpid,
        agentId: this.agentid,
        jsApiList: ['getCurExternalContact', 'getContext'],
        getAgentConfigSignature: async (url) => {
          const res = await getQwSignatureApi({ url, corpid: this.corpid, agentid: this.agentid });
          const { timestamp, nonce_str, signature } = res.content;
          return { timestamp, nonceStr: nonce_str, signature };
        },
      });
      ww.getCurExternalContact({
        success: (res) => {
          console.log('获取当前客户ID', res.userId);
          this.userId = res.userId;
          this.getUserInfo();
        },
        fail(err) {
          console.log('获取当前客户信息失败', err);
        },
      });
    },

    // ========== 用户信息 ==========
    async getUserInfo() {
      try {
        const res = await getUserInfoApi({
          corpid: this.corpid,
          external_userid: this.userId,
        });
        if (res && res.content) {
          const userList = (res.content.user_data_list || []).filter(Boolean);
          const yuewanList = (res.content.yuewan_user_data_list || []).filter(Boolean);
          this.userDataList = userList;
          this.yuewanUserDataList = yuewanList;
          if (userList.length > 0 || yuewanList.length > 0) {
            this.isGameBound = true;
            this.getVipInfo();
          } else {
            this.isGameBound = false;
            this.pageLoading = false;
          }
        } else {
          this.isGameBound = false;
          this.pageLoading = false;
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
        this.isGameBound = false;
        this.pageLoading = false;
      }
    },

    // ========== VIP 信息 ==========
    async getVipInfo() {
      try {
        const res = await getVipInfoApi({
          corpid: this.corpid,
          external_userid: this.userId,
          type: this.dataSource,
        });
        if (res && res.content) {
          const content = res.content;
          this.vipInfo = {
            vip_status: Number(content.vip_status),
            assign_time: content.assign_time,
            join_time: content.join_time,
            charge_person: content.charge_person,
            user_id: content.user_id,
            vip_id: content.vip_id,
          };
          this.vipRoleList = content.role_list || [];
          // VIP 的 role_list 为空时，从用户信息的 role_data_list 回退构建
          if (this.vipRoleList.length === 0) {
            const sourceList = this.dataSource === 'yuewan' ? this.yuewanUserDataList : this.userDataList;
            const roles = [];
            sourceList.forEach((user) => {
              (user.role_data_list || []).forEach((role) => {
                roles.push({
                  major_id: role.appkey,
                  major_name: role.pname,
                  role_id: role.role_id,
                  role_name: role.role_name,
                  role_create_time: role.role_create_time,
                });
              });
            });
            this.vipRoleList = roles;
          }
          const hasRoleList = this.vipRoleList.length > 0;
          const hasValidIds = content.user_id != null && content.vip_id != null;
          this.isGameBound = hasRoleList && hasValidIds;

          if (this.isGameBound && this.vipRoleList.length > 0) {
            this.selectedGame = this.vipRoleList[0].major_id;
            this.handleGameChange(this.selectedGame);
          }
        } else {
          this.isGameBound = false;
        }
      } catch (error) {
        console.error('获取VIP信息失败:', error);
        this.isGameBound = false;
      } finally {
        this.pageLoading = false;
      }
    },

    // ========== VIP 查询交互 ==========
    handleDataSourceChange() {
      this.selectedGame = '';
      this.selectedRole = '';
      this.showPromotionForm = false;
      this.resetPromotionForm();
      this.getVipInfo();
    },
    handleGameChange(majorId) {
      this.selectedRole = '';
      const roles = this.vipRoleList.filter((r) => r.major_id === majorId);
      if (roles.length > 0) {
        const sorted = roles.sort(
          (a, b) => new Date(b.role_create_time) - new Date(a.role_create_time)
        );
        this.selectedRole = sorted[0].role_id;
      }
    },
    async handleConfirmQuery() {
      if (!this.selectedGame) {
        this.$message.warning('请选择游戏');
        return;
      }
      if (!this.selectedRole) {
        this.$message.warning('请选择角色');
        return;
      }
      this.queryLoading = true;
      try {
        this.showPromotionForm = true;
        this.resetPromotionForm();
        await this.loadFacilitatorTypeOptions();
      } finally {
        this.queryLoading = false;
      }
    },

    // ========== 下拉选项加载 ==========
    async loadFacilitatorTypeOptions() {
      try {
        const res = await getFacilitatorTypeOptionsApi({
          external_userid: this.userId,
          corpid: this.corpid,
          type: this.dataSource,
        });
        if (res && res.content) {
          this.promoterList = res.content.facilitators || [];
          this.promotionTypeList = res.content.types || [];
        }
      } catch (error) {
        console.error('获取促单选项失败:', error);
      }
    },

    // ========== 时间选择与订单加载 ==========
    handleStartDateChange() {
      // 开始日期变更时清空结束日期，避免超出 7 天限制
      this.endDate = '';
      this.handleTimeChange();
    },
    syncChatLogTimeWithPromotion() {
      this.chatLogStartDate = this.startDate;
      this.chatLogStartTimeVal = this.startTimeVal || '00:00:00';
      this.chatLogEndDate = this.endDate;
      this.chatLogEndTimeVal = this.endTimeVal || '23:59:59';
    },
    handleChatLogStartDateChange() {
      this.chatLogEndDate = '';
    },
    handleChatLogTimeChange() {},
    handleTimeChange() {
      this.syncChatLogTimeWithPromotion();
      if (!this.startDate || !this.endDate) {
        this.orderData = [];
        this.activeDateTab = '';
        this.selectedOrders = [];
        this.selectAll = false;
        return;
      }
      const start = moment(this.fullStartTime);
      const end = moment(this.fullEndTime);
      if (!end.isAfter(start)) {
        this.$message.warning('结束时间必须大于开始时间');
        return;
      }
      this.loadOrderList();
    },
    async loadOrderList() {
      if (!this.fullStartTime || !this.fullEndTime) return;
      this.orderLoading = true;
      this.selectedOrders = [];
      this.selectAll = false;
      try {
        const res = await getOrderListApi({
          corpid: this.corpid,
          external_userid: this.userId,
          role_id: this.selectedRole,
          major_id: this.selectedGame,
          type: this.dataSource,
          start_time: this.fullStartTime,
          end_time: this.fullEndTime,
          user_id: this.vipInfo.user_id,
          vip_id: this.vipInfo.vip_id,
        });
        if (res && res.content) {
          this.orderData = res.content.order_list || [];
          if (this.orderData.length > 0) {
            this.activeDateTab = this.orderData[0].date;
          } else {
            this.activeDateTab = '';
          }
        }
      } catch (error) {
        console.error('获取订单列表失败:', error);
        this.$message.error('获取订单列表失败');
      } finally {
        this.orderLoading = false;
      }
    },

    // ========== 订单选择交互 ==========
    formatDateTab(date) {
      return moment(date).format('M/D');
    },
    // 格式化订单时间，只显示 HH:mm（日期已由 tab 表示）
    formatOrderTime(timeStr) {
      if (!timeStr) return '';
      return moment(timeStr).format('HH:mm');
    },
    // 获取指定日期的订单数量
    getDateOrderCount(date) {
      const group = this.orderData.find((g) => g.date === date);
      return group ? group.order.length : 0;
    },
    handleDateTabChange() {
      // 切换日期 tab，无需额外处理
    },
    isOrderSelected(orderSn) {
      return this.selectedOrders.some((o) => o.order_sn === orderSn);
    },
    handleOrderSelect(order) {
      const idx = this.selectedOrders.findIndex((o) => o.order_sn === order.order_sn);
      if (idx > -1) {
        this.selectedOrders.splice(idx, 1);
      } else {
        this.selectedOrders.push(order);
      }
    },
    handleDateSelectAll(checked) {
      const currentOrders = this.currentDateOrders;
      if (checked) {
        // 添加当天未选中的订单
        currentOrders.forEach((order) => {
          if (!this.isOrderSelected(order.order_sn)) {
            this.selectedOrders.push(order);
          }
        });
      } else {
        // 移除当天所有订单
        const currentSns = currentOrders.map((o) => o.order_sn);
        this.selectedOrders = this.selectedOrders.filter((o) => !currentSns.includes(o.order_sn));
      }
    },
    handleSelectAll(checked) {
      if (checked) {
        const allOrders = [];
        this.orderData.forEach((g) => g.order.forEach((o) => allOrders.push(o)));
        this.selectedOrders = allOrders;
      } else {
        this.selectedOrders = [];
      }
    },

    // ========== 提交促单 ==========
    async handleSubmit() {
      // 校验必填项
      if (!this.startDate || !this.endDate) {
        this.$message.warning('请选择促单发起/结算时间');
        return;
      }
      if (!this.chatLogStartDate || !this.chatLogEndDate) {
        this.$message.warning('请选择拉取会话时间');
        return;
      }
      const promotionStart = moment(this.fullStartTime);
      const promotionEnd = moment(this.fullEndTime);
      if (!promotionEnd.isAfter(promotionStart)) {
        this.$message.warning('促单发起/结算结束时间必须大于开始时间');
        return;
      }
      const chatLogStart = moment(this.fullChatLogStartTime);
      const chatLogEnd = moment(this.fullChatLogEndTime);
      if (!chatLogEnd.isAfter(chatLogStart)) {
        this.$message.warning('拉取会话结束时间必须大于开始时间');
        return;
      }
      if (chatLogStart.isAfter(promotionStart) || chatLogEnd.isBefore(promotionEnd)) {
        this.$message.warning('拉取会话时间需包含促单发起/结算时间');
        return;
      }
      if (this.selectedOrders.length === 0) {
        this.$message.warning('请选择订单');
        return;
      }
      if (!this.promoterId) {
        this.$message.warning('请选择促单发起人');
        return;
      }
      if (!this.promotionType) {
        this.$message.warning('请选择促单类型');
        return;
      }
      if (!this.promotionPlan || !this.promotionPlan.trim()) {
        this.$message.warning('请输入促单方案');
        return;
      }

      this.submitLoading = true;
      try {
        const res = await submitPromotionApi({
          corpid: this.corpid,
          external_userid: this.userId,
          role_id: this.selectedRole,
          majorId: this.selectedGame,
          type: this.dataSource,
          order: JSON.stringify(this.selectedOrders),
          facilitator_id: this.promoterId,
          type_id: this.promotionType,
          content: this.promotionPlan.trim(),
          start_time: this.fullStartTime,
          end_time: this.fullEndTime,
          chat_log_start_at: this.fullChatLogStartTime,
          chat_log_end_at: this.fullChatLogEndTime,
          money: this.promotionAmount,
          contact_userid: this.contactUserId,
          user_id: this.vipInfo.user_id,
          vip_id: this.vipInfo.vip_id,
        });
        if (res && res.ret === 1) {
          this.$message.success('提交成功');
          this.resetPromotionForm();
        } else {
          this.$message.error((res && (res.message || res.msg)) || '提交失败');
        }
      } catch (error) {
        console.error('提交促单失败:', error);
      } finally {
        this.submitLoading = false;
      }
    },

    // ========== 清空/重置 ==========
    handleClear() {
      this.resetPromotionForm();
    },
    resetPromotionForm() {
      this.startDate = '';
      this.startTimeVal = '00:00:00';
      this.endDate = '';
      this.endTimeVal = '23:59:59';
      this.chatLogStartDate = '';
      this.chatLogStartTimeVal = '00:00:00';
      this.chatLogEndDate = '';
      this.chatLogEndTimeVal = '23:59:59';
      this.orderData = [];
      this.activeDateTab = '';
      this.selectedOrders = [];
      this.selectAll = false;
      this.promoterId = '';
      this.promotionType = '';
      this.promotionPlan = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.promotion-wrapper {
  width: 360px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.promotion-container {
  padding: 15px;
  background-color: #f5f7fa;
  min-height: 100%;

  // 加载中提示
  .loading-tip {
    .tip-card {
      border-radius: 8px;

      .tip-content {
        text-align: center;
        padding: 30px 20px;

        i {
          font-size: 48px;
          color: #409eff;
          margin-bottom: 15px;
        }

        p {
          font-size: 14px;
          color: #606266;
          margin: 0;
        }
      }
    }
  }

  // 未绑定内联提示
  .not-bound-inline {
    text-align: center;
    padding: 20px 10px;

    i {
      font-size: 36px;
      color: #e6a23c;
      margin-bottom: 10px;
    }

    p {
      font-size: 14px;
      color: #909399;
      margin: 0;
    }
  }

  // 通用区块头部
  .section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;

    h3 {
      font-size: 18px;
      font-weight: bold;
      color: #303133;
      margin: 0;
    }
  }

  // 通用表单项
  .form-item {
    margin-bottom: 12px;

    .form-label {
      display: block;
      font-size: 14px;
      color: #606266;
      margin-bottom: 8px;
    }

    .datetime-row {
      display: flex;
      gap: 8px;

      .datetime-date {
        flex: 3;
      }

      .datetime-time {
        flex: 2;
      }

      ::v-deep .el-input__inner {
        padding-left: 30px;
        padding-right: 4px;
        font-size: 13px;
      }
    }
  }

  // VIP 查询区域
  .vip-query-section {
    margin-bottom: 15px;

    .query-card {
      border-radius: 8px;

      ::v-deep .el-radio-group {
        display: flex;
        width: 100%;

        .el-radio-button {
          flex: 1;

          .el-radio-button__inner {
            width: 100%;
            border-radius: 0;
            border-left-width: 0;
          }

          &:first-child .el-radio-button__inner {
            border-left-width: 1px;
            border-radius: 4px 0 0 4px;
          }

          &:last-child .el-radio-button__inner {
            border-radius: 0 4px 4px 0;
          }
        }
      }
    }
  }

  // 提交促单区域
  .promotion-form-section {
    margin-bottom: 15px;

    .promotion-card {
      border-radius: 8px;

      // 统一修正 checkbox 对勾居中
      ::v-deep .el-checkbox__inner {
        width: 18px;
        height: 18px;

        &::after {
          left: 5px;
          top: 1px;
          width: 5px;
          height: 10px;
        }
      }

      // 订单区域
      .order-section {
        margin-bottom: 12px;

        .order-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;

          .order-title {
            font-size: 14px;
            color: #303133;
            font-weight: 600;
          }

          .select-all-checkbox {
            ::v-deep .el-checkbox__label {
              font-size: 13px;
              color: #606266;
            }
          }
        }

        .date-tabs {
          margin-bottom: 10px;
          overflow-x: auto;

          .el-radio-group {
            display: flex;
            flex-wrap: nowrap;
          }

          .tab-count-badge {
            margin-left: 2px;

            ::v-deep .el-badge__content {
              height: 14px;
              line-height: 14px;
              padding: 0 4px;
              font-size: 10px;
              border: none;
              transform: translateY(-1px);
            }
          }
        }

        // 订单列表
        .order-list {
          border-radius: 8px;
          overflow: hidden;

          .date-select-all {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 10px;
            background: #f5f7fa;
            border-radius: 6px;
            margin-bottom: 8px;

            ::v-deep .el-checkbox__label {
              font-size: 12px;
              color: #909399;
            }

            .order-count {
              font-size: 12px;
              color: #909399;
            }
          }

          // 订单卡片（整行可点击）
          .order-card {
            display: flex;
            align-items: center;
            padding: 10px;
            margin-bottom: 6px;
            border: 1px solid #ebeef5;
            border-radius: 6px;
            background: #fff;
            cursor: pointer;
            transition: all 0.15s ease;
            -webkit-tap-highlight-color: transparent;

            &:last-child {
              margin-bottom: 0;
            }

            &:active {
              transform: scale(0.99);
            }

            &.selected {
              border-color: #b3d8ff;
              background: #f0f7ff;
            }

            .order-card-check {
              flex-shrink: 0;
              margin-right: 10px;
              display: flex;
              align-items: center;

              ::v-deep .el-checkbox {
                margin-right: 0;

                .el-checkbox__label {
                  display: none;
                }
              }
            }

            .order-card-body {
              flex: 1;
              min-width: 0;
              overflow: hidden;

              .order-card-main {
                display: flex;
                align-items: baseline;
                gap: 2px;

                .order-money {
                  font-size: 16px;
                  font-weight: 600;
                  color: #303133;
                }

                .order-money-unit {
                  font-size: 12px;
                  color: #909399;
                }
              }

              .order-card-sub {
                margin-top: 2px;
                font-size: 12px;
                color: #909399;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
            }

            .order-card-time {
              flex-shrink: 0;
              margin-left: 8px;
              font-size: 12px;
              color: #909399;
            }
          }

          .table-empty {
            text-align: center;
            padding: 24px 20px;
            color: #c0c4cc;

            i {
              font-size: 28px;
              display: block;
              margin-bottom: 8px;
            }

            p {
              font-size: 13px;
              margin: 0;
              color: #909399;
            }
          }
        }
      }

      .order-loading {
        text-align: center;
        padding: 20px;
        color: #909399;
        font-size: 13px;
      }

      // 促单金额
      .promotion-amount {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 14px;
        margin-bottom: 12px;
        background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%);
        border-left: 3px solid #409eff;
        border-radius: 0 8px 8px 0;

        .amount-left {
          display: flex;
          flex-direction: column;
          gap: 2px;

          .amount-label {
            font-size: 13px;
            color: #606266;
            font-weight: 500;
          }

          .amount-count {
            font-size: 11px;
            color: #909399;
          }
        }

        .amount-right {
          display: flex;
          align-items: baseline;
          gap: 2px;

          .amount-symbol {
            font-size: 14px;
            color: #409eff;
            font-weight: 500;
          }

          .amount-value {
            font-size: 22px;
            font-weight: 700;
            color: #409eff;
            font-variant-numeric: tabular-nums;
          }
        }
      }

      // 操作按钮
      .form-actions {
        display: flex;
        gap: 12px;
        margin-top: 4px;
      }
    }
  }
}
</style>
